/* Safe post-login return path validation.
 * The `next` parameter is a member-supplied redirect target. A bare
 * `startsWith('/')` check admits protocol-relative URLs ('//evil.example'),
 * backslash variants ('/\\evil.example' — URL parsers normalize '\\' to '/'),
 * and other authority-confusable forms. Canonical approach: require a
 * single leading '/', reject backslashes and whitespace/control characters
 * outright, then parse against the current origin and re-emit
 * path + query + fragment so only a verified same-origin app path ever
 * reaches the router. */

const MAX_LEN = 2048;
// ASCII control characters (incl. DEL) and whitespace — never valid in a
// canonicalised path and dangerous to pass through to navigation.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\x00-\x20\x7f]/;

export function safeReturnPath(raw: string | null | undefined, fallback = '/'): string {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LEN) {
    return fallback;
  }
  // Must be a root-relative path. '//' is protocol-relative (scheme-less
  // absolute URL) and '/\' confuses parsers that fold '\' to '/'.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) {
    return fallback;
  }
  // A backslash anywhere is ambiguous across URL parsers — reject rather
  // than risk one agent normalising it into an authority separator.
  if (raw.includes('\\')) return fallback;
  if (UNSAFE_CHARS.test(raw)) return fallback;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return fallback;
    // Re-emit only path + query + fragment — credentials, explicit ports
    // or any other URL component can never pass through.
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}
